import get from 'lodash/get';
import baseButtonStyles from '@converdy/c-components/BaseButton/BaseButton.styles';
import baseFieldStyles, {
  renderBaseFieldVars,
} from '@converdy/c-components/BaseField/BaseField.styles';
import headerStyles from '@converdy/c-components/CHeader/CHeader.styles';
import topbarStyles from '@converdy/c-components/CHeader/topbar.styles';
import CreateJssInstance from './CreateJssInstance';

import defaultsDeep from 'lodash/defaultsDeep';
import cloneDeep from 'lodash/cloneDeep';
import parseTypography from '@converdy/styles/parsers/parse-typography';

const jss = CreateJssInstance();

export const themeShapeMap = {
  pills: '60px',
  rounded: '6px',
  square: '0px',
};

export const colorKeyLabelMap = {
  primary: 'Primary',
  secondary: 'Secondary',
  primaryDark: 'Primary Dark',
  primaryLight: 'Primary Light',
  primary50: 'Primary 50% Opacity',
  primary50: 'Primary 50% Opacity',
  secondaryDark: 'Secondary Dark',
  secondaryLight: 'Secondary Light',
  secondary50: 'Secondary 50% Opacity',
  greyDefault: 'Grey Default',
  greyLight: 'Grey Light',
  greyDark: 'Grey Dark',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  white: 'White',
  headings: 'Headings',
  text: 'Text',
};

export const buttonVariantsLabelMap = {
  solid: 'Solid',
  outlined: 'Outlined',
  underlined: 'Underlined',
};

export const textVariantsLabelMap = {
  largeHeading: 'Large Heading',
  mediumHeading: 'Medium Heading',
  smallHeading: 'Small Heading',
  bodyText: 'Body Text',
  captionText: 'Caption Text',
};

export const fieldsVariantsLabelMap = {
  solid: 'Solid',
  outlined: 'Outlined',
  underlined: 'Underlined',
};

export const colorKeyVariableMap = Object.keys(colorKeyLabelMap).reduce(
  (obj, key) => {
    obj[key] = `--color-${key}`;
    return obj;
  },
  {},
);

export const colorVariableKeyMap = Object.keys(colorKeyLabelMap).reduce(
  (obj, key) => {
    obj[`--color-${key}`] = key;
    return obj;
  },
  {},
);

export const computedTheme = (theme = {}, defaultTheme = {}) => {
  return defaultsDeep(cloneDeep(theme), cloneDeep(defaultTheme));
};

/* Should get the computed theme */
export function getThemeJss(theme, scope = 'body') {
  const colorCustomProperties = Object.keys(colorVariableKeyMap).reduce(
    (obj, key) => {
      obj[key] = get(theme, `colors.${colorVariableKeyMap[key]}`);
      return obj;
    },
    {},
  );

  let returnJss = {
    /* Needed for access to color properties outside of .scope */
    ':root': {
      ...colorCustomProperties,
      '--color-cookieNoticeBackground': theme.cookie.background,
      '--color-cookieNoticeText': theme.cookie.text,
      '--color-cookieNoticeButton': theme.cookie.button,
      // 'scroll-behavior': theme.scrollBehavior,
      // CInspiration
      '--primary-grid-color': theme.CInspiration.gridColor,
      '--cinspiration-text-color': theme.CInspiration.text,
      '--cinspiration-title-color': theme.CInspiration.title,
    },

    [`${scope}`]: {
      ...renderBaseFieldVars(theme.baseField),
      ...getFontsVariable(theme),
      '--themeShape': themeShapeMap[theme.themeShape],
      '--themeBorderThickness': theme.themeBorderThickness,
      ...parseTypography(
        theme.variants.text.bodyText,
      ) /* Render body text styling */,
      '-webkitFontSmoothing': 'antialiased',

      // Topbar Styles
      ...topbarStyles(theme.header.topbar),
      // Header
      ...headerStyles(theme.header),
    },
    [`${scope} a`]: {
      color: 'var(--color-primary)',
    },
    [`${scope} .baseButton`]: {
      ...parseTypography(theme.buttonTypography),
    },
    [`${scope} .baseFields [placeholder]`]: {
      '&::placeholder': parseTypography(
        theme.formPlaceholder,
      ) /* todo probs not working */,
    },
    [`${scope} .baseLabel`]: {
      ...parseTypography(theme.formLabel),
    },
    ...generateThemeElementVariantClasses(theme.variants),
  };

  return {
    '@global': {
      ...returnJss,
    },
  };
}

/* 
  Accepts theme, returns object with custom properties which define --primary-font, --secondary-font,
  etc with the corrensponding value 
*/
export function getFontsVariable({fonts}) {
  const systemFont =
    '-apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol';
  return Object.entries(fonts).reduce((prev, [key, value]) => {
    let fontFamily =
      value.type === 'google'
        ? value.googleFont
        : value.type === 'custom'
        ? value.name
        : systemFont;
    prev[`--font-${key}`] = fontFamily;
    return prev;
  }, {});
}

/* Get Google Fonts Link */
export function getGoogleFontsLink(fonts) {
  const googleFonts = Object.values(fonts).reduce((prev, current) => {
    if (current.type === 'google' && !prev.includes(current.googleFont)) {
      prev.push(current.googleFont);
    }
    return prev;
  }, []);

  const googleFontsString = googleFonts.reduce(
    (fontsString, current, index) => {
      if (index === 0) {
        fontsString += `?family=${current}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900`;
      } else {
        fontsString += `&family=${current}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900`;
      }
      return fontsString;
    },
    `https://fonts.googleapis.com/css2`,
  );

  return googleFonts.length > 0 ? googleFontsString + '&display=swap' : false;
}

export function getCustomFontsLink(_fonts) {
  const toBase64 = (txt) => {
    if (typeof process === 'object')
      return eval(`Buffer.from(\`${txt}\`, 'utf8').toString('base64')`);
    else return eval(`btoa(\`${txt}\`)`);
  };
  const fonts = Object.values(_fonts);
  if (!fonts || !fonts.find((f) => f.type == 'custom')) return '';
  return `data:text/css;base64,${toBase64(
    fonts.reduce((a, v) => {
      if (v.type == 'custom')
        a += `@font-face { font-family: '${v.name}'; src: url(${
          v.url
        }) format('${
          v.extension == 'ttf'
            ? 'truetype'
            : v.extension == 'otf'
            ? 'opentype'
            : v.extension == 'woff'
            ? 'woff'
            : v.extension == 'woff2'
            ? 'woff2'
            : ''
        }'); }`;
      return a;
    }, ''),
  )}`;
}

/* 
Returns a jss stylesheet
*/
export const getJssStyleSheet = (theme, scope) => {
  return jss.createStyleSheet(getThemeJss(theme, scope), {
    index: 1,
    meta: 'global-theme',
  });
};

/* 
Returns a css string
*/
export const getThemeCss = (theme) => {
  return getJssStyleSheet(theme).toString();
};

function generateThemeElementVariantClasses(themeVariants) {
  return Object.entries(themeVariants).reduce((obj, element) => {
    const [key, variants] = element;
    /* Todo this could be a reduces over object.entries as well...*/
    Object.keys(variants).forEach((variant) => {
      // obj[`.${key}.${variant}`] = variants[variant]
      obj[`.${key}.${variant}`] = parseVariantOptionsToJss(
        key,
        Object.assign({variant: variant}, variants[variant]),
      );
    });

    return obj;
  }, {});
}

const variantsToJssParserMap = {
  baseButton: buttonStylesToJss,
  text: parseTypography,
  baseFields: fieldStylesToJss,
};

function parseVariantOptionsToJss(variantKey, options) {
  if (variantsToJssParserMap[variantKey]) {
    return variantsToJssParserMap[variantKey](options);
  }

  return {};
}

function buttonStylesToJss(props) {
  let jss = {
    ...baseButtonStyles(props),
  };
  return jss;
}

function fieldStylesToJss(props) {
  let jss = {
    ...baseFieldStyles(props),
  };
  return jss;
}
